import { useEffect, useState } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { buildHead } from "@/lib/seo";

const GREEN = "#1f1f1f";
type LoginSearch = { redirect?: string };

function safeRedirect(target: string | undefined): string {
  if (!target || !target.startsWith("/") || target.startsWith("//")) return "/partner";
  return target;
}

export const Route = createFileRoute("/partner-login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({ redirect: typeof search.redirect === "string" ? search.redirect : undefined }),
  head: () => buildHead({ locale: "en", title: "Referral partner sign in · Shugert", description: "Access the Shugert referral partner portal.", path: "/partner-login", altPath: "/partner-login", noindex: true }),
  component: PartnerLoginPage,
});

function PartnerLoginPage() {
  const router = useRouter();
  const { redirect } = Route.useSearch();
  const redirectTo = safeRedirect(redirect);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => { if (active && data.session?.user) router.navigate({ to: redirectTo }); });
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => { if (session?.user) router.navigate({ to: redirectTo }); });
    return () => { active = false; subscription.subscription.unsubscribe(); };
  }, [redirectTo, router]);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setState("sending");
    setError("");
    const { error: authError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}${redirectTo}` },
    });
    if (authError) { setError(authError.message); setState("error"); return; }
    setState("sent");
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md rounded-[1.75rem] border border-border bg-card/35 p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: GREEN }}>Referral partner portal</p>
        <h1 className="mt-4 font-display text-4xl font-semibold tracking-[-0.04em]">Track referrals and commissions.</h1>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">Use the email approved for your Shugert referral partner account. We will send a one-time sign-in link.</p>
        {state === "sent" ? (
          <div className="mt-8 rounded-xl border border-emerald-700/25 bg-emerald-700/[0.055] p-5 text-sm"><strong>Check your email.</strong><p className="mt-2 text-xs text-muted-foreground">A sign-in link was sent to {email}.</p></div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Approved partner email<input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} className="form-input mt-2" /></label>
            {state === "error" ? <p className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</p> : null}
            <button disabled={state === "sending"} className="w-full rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background disabled:opacity-50">{state === "sending" ? "Sending…" : "Send magic link"}</button>
          </form>
        )}
        <div className="mt-8 space-y-2 text-xs text-muted-foreground"><p>Not approved yet? <Link to="/partners/referrals" className="font-semibold text-foreground underline">Apply to the program</Link>.</p><p>Need help? <a href="mailto:hello@shugert.com.mx" className="underline">hello@shugert.com.mx</a></p></div>
      </div>
    </div>
  );
}
